import { meStore, useStoreDashboardStats } from '@gozoki/api'
import type { StoreDailyPoint, StoreMetricSeries } from '@gozoki/api-types'
import { frenchFormat } from '@gozoki/tools'
import { cn } from '@gozoki/ui/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import closeIcon from '~/assets/dashboard/close.svg'
import { ModalCloseButton } from './ui/ModalCloseButton'
import { TradeUpIcon } from './icons/dashboard-icons'

/** Clés des scores détaillés par les cartes du bas ; `null` = courbe de CA (état par défaut du ✕). */
type MetricKey = 'fifoScore' | 'tauxCasse' | 'tauxVol'

const CHART_HEIGHT = 280
const BAR_AREA_HEIGHT = 52

const signed = (value: number) => `${value > 0 ? '+' : ''}${value.toString().replace('.', ',')}`
const oneDecimal = (value: number) => value.toFixed(1).replace('.', ',')
const round2 = (value: number) => Math.round(value * 100) / 100

interface MetricDefinition {
  /** Libellé du chip et de la légende. */
  label: string
  cardTitle: string
  /** Les ratios arrivent en 0-1 : le graphe et les cartes travaillent en points de %. */
  toDisplay: (value: number) => number
  formatValue: (value: number) => string
  formatAxis: (value: number) => string
  /** Sens de lecture de la variation : le FIFO doit monter, la casse et le vol descendre. */
  higherIsBetter: boolean
  /** Variation vs S-1, dans l'unité déjà utilisée par les scores du dashboard. */
  formatTrend: (trend: number) => string
}

const METRICS: Record<MetricKey, MetricDefinition> = {
  fifoScore: {
    label: 'Score FIFO',
    cardTitle: 'Score FIFO',
    toDisplay: (value) => value,
    formatValue: (value) => `${Math.round(value)} / 100`,
    formatAxis: (value) => `${Math.round(value)}`,
    higherIsBetter: true,
    formatTrend: (trend) => signed(Math.round(trend)),
  },
  tauxCasse: {
    label: 'Taux de casse',
    cardTitle: 'Taux de casse',
    toDisplay: (value) => value * 100,
    formatValue: (value) => `${oneDecimal(value)} %`,
    formatAxis: (value) => `${oneDecimal(value)}`,
    higherIsBetter: false,
    formatTrend: (trend) => `${signed(Math.round(trend * 1000) / 10)}%`,
  },
  tauxVol: {
    label: 'Taux de vol',
    cardTitle: 'Taux de vol',
    toDisplay: (value) => value * 100,
    formatValue: (value) => `${oneDecimal(value)} %`,
    formatAxis: (value) => `${oneDecimal(value)}`,
    higherIsBetter: false,
    formatTrend: (trend) => `${signed(Math.round(trend * 1000) / 10)}%`,
  },
}

const CA_METRIC: MetricDefinition = {
  label: 'Chiffre d’affaires',
  cardTitle: 'Chiffre d’affaires',
  toDisplay: (value) => value,
  formatValue: (value) => `${frenchFormat(Math.round(value))} €`,
  formatAxis: (value) => frenchFormat(Math.round(value)),
  higherIsBetter: true,
  formatTrend: (trend) => `${signed(Math.round(trend))}%`,
}

const shortDay = (isoDate: string) => {
  const day = DateTime.fromISO(isoDate).setLocale('fr').toFormat('ccc').replace('.', '')
  return day.charAt(0).toUpperCase() + day.slice(1)
}

const frenchDate = (isoDate: string | undefined) =>
  isoDate ? DateTime.fromISO(isoDate).toFormat('dd/MM/yyyy') : '—'

interface ChartRow {
  label: string
  current: number | null
  previous: number | null
}

/**
 * Aligne les deux fenêtres jour à jour : la série S-1 est indexée par position, pas par date —
 * ses dates sont celles de la semaine précédente et ne doivent pas piloter l'axe.
 */
const buildChartRows = (series: StoreMetricSeries, metric: MetricDefinition): ChartRow[] =>
  series.current.map((point: StoreDailyPoint, index: number) => {
    const previous = series.previous[index]?.value ?? null
    return {
      label: shortDay(point.date),
      current: point.value === null ? null : metric.toDisplay(point.value),
      previous: previous === null ? null : metric.toDisplay(previous),
    }
  })

// Pastille de variation : verte quand l'indicateur va dans le bon sens, orange sinon.
function TrendPill({
  trend,
  metric,
  size = 'sm',
}: {
  trend: number | null | undefined
  metric: MetricDefinition
  size?: 'sm' | 'lg'
}) {
  const value = trend ?? null
  const isGood = value === null || value === 0 || metric.higherIsBetter === value > 0
  // `>` lie plus fort que `===` : on compare bien `higherIsBetter` au signe de la variation.

  return (
    <span
      className={cn(
        'flex shrink-0 items-center rounded-full border',
        isGood ? 'border-[#DAF6CD] bg-[#E7FFDA]' : 'border-[#FFE2CF] bg-[#F4EBE5]',
        size === 'lg' ? 'gap-[14px] py-[14px] pr-[14px] pl-[20px]' : 'gap-[6px] px-[10px] py-[7px]',
      )}
    >
      <span
        className={cn(
          'flex items-center justify-center',
          isGood ? 'text-[#4F6A47]' : 'text-[#EF8031]',
          size === 'lg' ? 'size-[47px]' : 'h-[16px] w-[22px]',
          (value ?? 0) < 0 && 'rotate-x-180',
        )}
      >
        <TradeUpIcon />
      </span>
      <span
        className={cn('flex flex-col items-start', isGood ? 'text-[#4F6A47]' : 'text-[#EF8031]')}
      >
        <span className={cn('font-sans font-bold', size === 'lg' ? 'text-[13px]' : 'text-[11px]')}>
          {value === null ? '—' : metric.formatTrend(value)}
        </span>
        <span className={cn('font-sans', size === 'lg' ? 'text-[10px]' : 'text-[10px]')}>
          vs S-1
        </span>
      </span>
    </span>
  )
}

// Carte d'un score : valeur de la fenêtre, variation, et histogramme des 7 jours.
function ScoreCard({
  metricKey,
  value,
  trend,
  series,
  active,
  onSelect,
}: {
  metricKey: MetricKey
  value: number | null | undefined
  trend: number | null | undefined
  series: StoreMetricSeries | undefined
  active: boolean
  onSelect: () => void
}) {
  const metric = METRICS[metricKey]
  const points = series?.current ?? []
  const displayed = points.map((point) =>
    point.value === null ? null : metric.toDisplay(point.value),
  )
  const max = Math.max(...displayed.map((entry) => entry ?? 0), 0)

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex flex-1 cursor-pointer flex-col gap-[8px] rounded-[32px] p-[18px] text-left',
        active ? 'border-2 border-[#507247] bg-[#E7FFDA]' : 'border border-[#D7D7D7] bg-[#F6F6F6]',
      )}
      aria-pressed={active}
    >
      <div className="flex w-full items-start justify-between gap-[8px]">
        <span className="flex flex-col gap-[8px]">
          <span className="font-sans text-[13px] font-semibold text-[#072C1A] uppercase">
            {metric.cardTitle}
          </span>
          <span className="font-sans text-[22px] leading-[22px] font-bold text-[#072C1A]">
            {value === null || value === undefined
              ? '—'
              : metric.formatValue(metric.toDisplay(value))}
          </span>
        </span>
        <TrendPill trend={trend} metric={metric} />
      </div>

      <div className="flex h-[52px] w-full items-end gap-[6px]">
        {displayed.map((entry, index) => (
          <span
            key={index}
            className="min-w-0 flex-1 rounded-t-[3px] bg-[#5E7D4F]"
            style={{ height: max > 0 && entry ? Math.max(4, (entry / max) * BAR_AREA_HEIGHT) : 0 }}
          />
        ))}
      </div>

      <div className="flex w-full items-start justify-between">
        {points.map((point) => (
          <span key={point.date} className="font-sans text-[9px] text-[#8A887F]">
            {shortDay(point.date)}
          </span>
        ))}
      </div>
    </button>
  )
}

function LegendMark({ dashed }: { dashed: boolean }) {
  return (
    <span className="relative flex h-[12px] w-[36px] items-center">
      <span
        className={cn(
          'w-full border-t-2',
          dashed ? 'border-dashed border-[#7FA173]' : 'border-solid border-[#507247]',
        )}
      />
      <span
        className={cn(
          'absolute left-1/2 size-[8px] -translate-x-1/2 rounded-full border-2 bg-[#F0EDE2]',
          dashed ? 'border-[#7FA173]' : 'border-[#507247]',
        )}
      />
    </span>
  )
}

/**
 * Modale « CA boutique » (Figma 11131:22345), ouverte depuis le chevron de la carte C.A 7 JOURS.
 *
 * Rendue DANS le canvas mis à l'échelle, comme `CamerasModal` : le `Dialog` partagé porte sur
 * `document.body`, donc hors du `transform: scale()` du dashboard.
 *
 * Elle ne déclenche aucune requête : `useStoreDashboardStats` partage sa clé React Query avec la
 * carte qui l'ouvre, et `meStore` celle de `HomePage`.
 */
export function CaDetailModal({ onClose }: { onClose: () => void }) {
  const { data } = useStoreDashboardStats()
  const { data: me } = useQuery({ queryKey: ['store', 'me'], queryFn: meStore })
  // Le Figma ouvre sur le Score FIFO ; le ✕ du chip revient au CA, sujet de la modale.
  const [selectedMetric, setSelectedMetric] = useState<MetricKey | null>('fifoScore')

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const caChart = data?.caChart
  const sales = data?.sales
  const scores = data?.scores

  const metric = selectedMetric ? METRICS[selectedMetric] : CA_METRIC
  const series: StoreMetricSeries | undefined = selectedMetric
    ? data?.scoresChart[selectedMetric]
    : caChart && {
        current: caChart.current.map((point) => ({ date: point.date, value: point.ca })),
        previous: caChart.previous.map((point) => ({ date: point.date, value: point.ca })),
      }

  const rows = series ? buildChartRows(series, metric) : []

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-[32px]"
      role="dialog"
      aria-modal="true"
      aria-label="Détail du chiffre d'affaires"
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-[1090px] max-w-full flex-col gap-[16px] overflow-y-auto kiosk-scroll-y rounded-[20px] bg-[#F0EDE2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-[12px]">
          <div className="flex min-w-0 items-center gap-[12px]">
            <h2 className="truncate font-sans text-[22px] leading-[24px] font-semibold text-[#181D27] uppercase">
              CA boutique {me?.store.label ?? ''}
            </h2>
            <span className="shrink-0 rounded-full border border-[#506B48] bg-[#F6F6F6] px-[15px] py-[9px] font-brand text-[16px] font-semibold whitespace-nowrap text-[#0C3A2D] uppercase">
              7 jours glissants
            </span>
          </div>

          <ModalCloseButton onClose={onClose} ariaLabel="Fermer le détail du CA" />
        </div>

        <p className="font-sans text-[13px] text-[#666]">
          Chiffres du {frenchDate(caChart?.current[0]?.date)} au{' '}
          {frenchDate(caChart?.current.at(-1)?.date)} - Comparaison avec les chiffres du{' '}
          {frenchDate(caChart?.previous[0]?.date)} au {frenchDate(caChart?.previous.at(-1)?.date)}
        </p>

        <div className="flex items-start gap-[36px]">
          <div className="flex flex-col gap-[12px]">
            <span className="font-sans text-[40px] leading-[40px] font-bold text-[#072C1A]">
              {caChart ? `${frenchFormat(Math.round(caChart.totalCa))} €` : '—'}
            </span>
            <div className="flex items-start gap-[32px]">
              <span className="flex flex-col">
                <span className="font-sans text-[12px] font-medium text-[#072C1A] uppercase">
                  Panier moyen
                </span>
                <span className="font-sans text-[16px] font-semibold text-[#072C1A]">
                  {sales?.averageCart != null
                    ? `${frenchFormat(round2(sales.averageCart))} €`
                    : '—'}
                </span>
              </span>
              <span className="flex flex-col">
                <span className="font-sans text-[12px] font-medium text-[#072C1A] uppercase">
                  Tickets
                </span>
                <span className="font-sans text-[16px] font-semibold text-[#072C1A]">
                  {sales ? frenchFormat(sales.ticketCount) : '—'}
                </span>
              </span>
            </div>
          </div>

          <TrendPill trend={caChart?.vsPrevWeekPercent} metric={CA_METRIC} size="lg" />
        </div>

        {selectedMetric ? (
          <div className="flex items-center">
            <span className="flex items-center gap-[8px] rounded-full border border-[#DAF6CD] bg-[#E7FFDA] py-[7px] pr-[9px] pl-[15px]">
              <span className="font-sans text-[12px] font-semibold text-[#4F6A47]">
                Courbe : {metric.label}
              </span>
              <button
                type="button"
                onClick={() => setSelectedMetric(null)}
                aria-label="Revenir à la courbe de chiffre d'affaires"
                className="flex size-[18px] cursor-pointer items-center justify-center rounded-full bg-white"
              >
                <img src={closeIcon} alt="" aria-hidden className="size-[10px]" />
              </button>
            </span>
          </div>
        ) : null}

        <div className="w-full" style={{ height: CHART_HEIGHT }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="caDetailArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#507247" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#507247" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid horizontal={false} strokeDasharray="4 4" stroke="rgba(0,0,0,0.25)" />
              <YAxis
                width={48}
                domain={['auto', 'auto']}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: 'rgba(0,0,0,0.7)' }}
                tickFormatter={metric.formatAxis}
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: '#1F4B15', fontWeight: 700 }}
              />
              <Area
                type="monotone"
                dataKey="current"
                stroke="none"
                fill="url(#caDetailArea)"
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="previous"
                stroke="#7FA173"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={{ r: 4, fill: '#F0EDE2', stroke: '#7FA173', strokeWidth: 2 }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="current"
                stroke="#507247"
                strokeWidth={2.5}
                dot={{ r: 4, fill: '#FFFFFF', stroke: '#507247', strokeWidth: 2 }}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="flex items-center justify-center gap-[32px]">
          <span className="flex items-center gap-[8px]">
            <LegendMark dashed={false} />
            <span className="font-sans text-[12px] text-[#2A2A2A]">{metric.label}</span>
          </span>
          <span className="flex items-center gap-[8px]">
            <LegendMark dashed />
            <span className="font-sans text-[12px] text-[#2A2A2A]">{metric.label} S-1</span>
          </span>
        </div>

        <div className="flex items-start gap-[12px]">
          <ScoreCard
            metricKey="fifoScore"
            value={scores?.fifoScore}
            trend={scores?.fifoTrend}
            series={data?.scoresChart.fifoScore}
            active={selectedMetric === 'fifoScore'}
            onSelect={() => setSelectedMetric('fifoScore')}
          />
          <ScoreCard
            metricKey="tauxCasse"
            value={scores?.tauxCasse}
            trend={scores?.casseTrend}
            series={data?.scoresChart.tauxCasse}
            active={selectedMetric === 'tauxCasse'}
            onSelect={() => setSelectedMetric('tauxCasse')}
          />
          <ScoreCard
            metricKey="tauxVol"
            value={scores?.tauxVol}
            trend={scores?.volTrend}
            series={data?.scoresChart.tauxVol}
            active={selectedMetric === 'tauxVol'}
            onSelect={() => setSelectedMetric('tauxVol')}
          />
        </div>
      </div>
    </div>
  )
}
